import { EditorState, convertToRaw } from "draft-js";
import {
  ArrayHelpers,
  FieldArray,
  Formik,
  FormikHelpers,
  FormikProps,
  Form,
  FormikErrors
} from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import {
  MenuItem,
  Button,
  FormControl,
  Select as MuiSelect,
  makeStyles
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { Alert } from "@material-ui/lab";

import { useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { addCalleeFieldToCampaign } from "src/graphql/campaignCalleeFields";
import { EDIT_CAMPAIGN_QUESTIONS } from "src/graphql/campaignQuestions";
import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import {
  campaignCreateCalleeField,
  campaignCreateCalleeFieldVariables
} from "src/graphql/generated/campaignCreateCalleeField";
import {
  campaignEditQuestions,
  campaignEditQuestionsVariables,
  campaignEditQuestions_campaignEditQuestions_questions
} from "src/graphql/generated/campaignEditQuestions";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  CampaignQuestionInput,
  PollingPlaceLookupTool,
  QuestionType
} from "src/graphql/generated/globalTypes";
import { loggedInInfo_loggedInInfo_tenant } from "src/graphql/generated/loggedInInfo";
import { getSegmentCount, smartEncodeReplace } from "src/util/messaging";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "../CampaignEditorsProvider";
import { FormikSaveButtons } from "../SaveButtons";
import Question, {
  QUESTION_TYPES_WITH_ANSWERS,
  DISPOSITION_ACTION_TITLES
} from "./Question";
import {
  editorStateToPlainText,
  emptyEditorState,
  plainTextToInitialEditorState
} from "./TextEditor/serialization";
import { FormAnswer } from "./types";
import { FormQuestion, Values } from "./types";

const FIRST_QUESTION_KEY = "disposition";

const MAX_MESSAGE_SEGMENT_COUNT = 8;

const DEFAULT_POLLING_PLACE_LOOKUP_TOOL = PollingPlaceLookupTool.IWILLVOTE;

const useStyles = makeStyles(theme => ({
  questionNavigation: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
    fontSize: "1.5em",

    "@media(max-width: 1100px)": {
      flexDirection: "column"
    }
  },
  questionDropdown: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginTop: "5px",
    flex: 1,

    "@media(max-width: 1100px)": {
      flex: 0
    },

    "& .MuiSelect-selectMenu": {
      whiteSpace: "normal"
    }
  },
  errorMenuItem: {
    color: theme.palette.error.main
  },
  alert: {
    marginBottom: theme.spacing(3)
  }
}));

const cleanVanFieldValue = (
  value: number | "" | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
};

const MOBILIZEAMERICA_URL_REGEX = /mobilize.us\/([^/]+)\/event\/(\d+)/;
const getMobilizeAmericaEventParams = (
  url: string
): { eventId: number; mobilizeTenantSlug: string } => {
  const match = url.match(MOBILIZEAMERICA_URL_REGEX);
  if (!match) {
    throw new Error(`Invalid MobilizeAmerica URL: ${url}`);
  }

  return {
    mobilizeTenantSlug: match[1],
    eventId: parseInt(match[2], 10)
  };
};

const questionWithAnswers = (val: QuestionType) =>
  [
    QuestionType.DISPOSITION,
    QuestionType.SINGLE_CHOICE,
    QuestionType.MULTIPLE_CHOICE,
    QuestionType.PATCH_THRU
  ].includes(val);

const dispositionQuestion = (val: QuestionType) =>
  val === QuestionType.DISPOSITION;
const validateDispositionFor = (disposition: string) =>
  yup
    .array()
    .test(
      `has-required-disposition-${disposition}`,
      `At least one answer must use the '${DISPOSITION_ACTION_TITLES[disposition]}' disposition`,
      value => _.find(value, answer => answer.disposition === disposition)
    );

const canvassedAnswerWithNext = (answer: FormAnswer) =>
  answer.disposition === "CANVASSED" && answer.next;

const schemaForTenant = (
  tenant: loggedInInfo_loggedInInfo_tenant,
  ivr: boolean,
  { delayNonIVRValidations }: { delayNonIVRValidations?: boolean } = {}
) =>
  yup.object().shape({
    questions: yup.array().of(
      yup
        .object()
        .shape({
          name: yup.string().required("Required"),
          questionType: yup
            .mixed()
            .oneOf(_.values(QuestionType))
            .required("Required")
            .test(
              `has-only-ivr-compatible-questions-if-ivr-campaign`,
              `This question type is unsupported in phone-only mode`,
              value =>
                !ivr ||
                !value ||
                [QuestionType.DISPOSITION, QuestionType.SINGLE_CHOICE].includes(
                  value
                )
            ),
          script: yup.mixed(),
          answers: yup
            .array()
            .when("questionType", {
              is: questionWithAnswers,
              then: yup
                .array()
                .of(
                  yup.object().shape({
                    value: yup.string().required("Required"),
                    next: yup.string().nullable()
                  })
                )
                .min(1, "Must have at least one answer")
            })
            .when("questionType", {
              is: dispositionQuestion,
              then: yup.array().of(
                yup.object().shape({
                  value: yup.string().required("Required"),
                  next: yup.string().nullable(),
                  disposition: yup.string().required("Required")
                })
              )
            })
            .test(
              `has-only-8-answers-if-ivr-campaign`,
              `A maximum of 8 answers are supported in phone-only mode`,
              answers => {
                return !ivr || !!(answers && answers.length < 9);
              }
            )
            .when("questionType", {
              is: dispositionQuestion,
              then: validateDispositionFor("ANSWERING_MACHINE")
            })
            .when("questionType", {
              is: dispositionQuestion,
              then: validateDispositionFor("NO_CALLBACK")
            })
            .when("questionType", {
              is: dispositionQuestion,
              then: validateDispositionFor("CALLBACK")
            })
            .when("questionType", {
              is: dispositionQuestion,
              then: validateDispositionFor("CANVASSED")
            })
            .when("questionType", {
              is: dispositionQuestion,
              then: yup.array().test(
                `canvassed-question-must-have-followup`,
                `All Canvassed answers must have a followup question`,
                value =>
                  _.find(value, canvassedAnswerWithNext) ||
                  // By default, questions start in an invalid state because of
                  // this validation. Allow it to be bypassed on the Basics step
                  // when phone-only mode is enabled. Otherwise the user will get
                  // stuck on the first step.
                  delayNonIVRValidations
              )
            })
            .when("questionType", {
              is: (val: QuestionType) => val === QuestionType.PATCH_THRU,
              then: yup.array().of(
                yup.object().shape({
                  patchThruConfig: yup
                    .object()
                    .nullable()
                    .shape({ fixedNumber: yup.string().nullable() })
                    .when("next", {
                      is: (val: string) => val === QuestionType.PATCH_THRU,
                      then: yup.object().shape({
                        fixedNumber: yup
                          .string()
                          .required("Required")
                          .phoneNumber(tenant?.countryIso)
                      })
                    })
                })
              )
            }),
          mobilizeAmericaEventUrl: yup.string().when("questionType", {
            is: QuestionType.MOBILIZEAMERICA_RSVP,
            then: yup
              .string()
              .required("Required")
              .matches(
                MOBILIZEAMERICA_URL_REGEX,
                "Must be a MobilizeAmerica event URL"
              )
          }),
          sendMessageText: yup.mixed().when("questionType", {
            is: QuestionType.SEND_MESSAGE,
            then: yup
              .mixed()
              .test(
                "notEmpty",
                "Required",
                (value?: EditorState) =>
                  !!value && editorStateToPlainText(value).length > 0
              )
              .required("Required")
              .test(
                "messageLength",
                "Message cannot exceed 8 segments",
                (value?: EditorState) => {
                  if (!value) {
                    return true;
                  }
                  const text = editorStateToPlainText(value);
                  // Replace callee fields with shorter sample text so that the
                  // segment estimate is more realistic (rather than the
                  // excessive 53 characters of `{callee_field:...}`).
                  const textWithReplacedFields = text.replace(
                    /{callee_field:[\w:-]+}/g,
                    "CALLEE_FIELD_SAMPLE_TEXT"
                  );
                  const cleanedText = smartEncodeReplace(
                    textWithReplacedFields
                  );
                  return (
                    getSegmentCount(cleanedText) <= MAX_MESSAGE_SEGMENT_COUNT
                  );
                }
              )
          }),
          pollingPlaceLookupTool: yup.mixed().when("questionType", {
            is: QuestionType.POLLING_PLACE_LOOKUP,
            then: yup
              .string()
              .required("Required")
              .oneOf(
                Object.values(PollingPlaceLookupTool),
                "Invalid lookup tool"
              )
          }),
          next: yup.string().nullable()
        })
        .test(
          "does-not-have-invalid-fields",
          "Must not have any missing fields",
          ({ script }: { script?: string | EditorState }) => {
            if (!script || typeof script === "string") return true;
            const entities = _.values(
              convertToRaw((script as EditorState).getCurrentContent())
                .entityMap
            );
            return !entities.find(entity => !!entity?.data.isInvalid);
          }
        )
    )
  });

export const validateQuestionsWithIVR = (
  tenant: loggedInInfo_loggedInInfo_tenant,
  campaign: getCampaign_campaign
): Promise<boolean> =>
  schemaForTenant(tenant, true, {
    delayNonIVRValidations: true
  }).isValid({
    questions: campaign.questions
  });

const rawQuestionToFormValues = (
  rawQuestion: campaignEditQuestions_campaignEditQuestions_questions,
  campaignCalleeFields: CampaignWithCalleeFields_calleeFields[]
): FormQuestion => ({
  ...rawQuestion,
  script: rawQuestion.script
    ? plainTextToInitialEditorState(
        rawQuestion.script,
        campaignCalleeFields,
        rawQuestion.isRichText
      )
    : emptyEditorState(),
  mobilizeAmericaEventUrl: rawQuestion.mobilizeAmericaRsvpSettings
    ? `https://www.mobilize.us/${rawQuestion.mobilizeAmericaRsvpSettings.mobilizeTenantSlug}/event/${rawQuestion.mobilizeAmericaRsvpSettings.eventId}/`
    : "",
  sendMessageText: rawQuestion.sendMessageSettings?.text
    ? plainTextToInitialEditorState(
        rawQuestion.sendMessageSettings.text,
        campaignCalleeFields,
        false
      )
    : emptyEditorState(),
  pollingPlaceLookupTool:
    rawQuestion.pollingPlaceLookupSettings?.lookupTool ??
    DEFAULT_POLLING_PLACE_LOOKUP_TOOL
});

const formikValues = (campaign: getCampaign_campaign) =>
  campaign.questions.length > 0
    ? campaign.questions.map(question =>
        rawQuestionToFormValues(question, campaign.calleeFields)
      )
    : [
        {
          __typename: "CampaignQuestion" as const,
          key: FIRST_QUESTION_KEY,
          name: "",
          script: emptyEditorState(),
          isRichText: true,
          questionType: QuestionType.SINGLE_CHOICE,
          answers: [],
          next: "",
          mobilizeAmericaEventUrl: "",
          sendMessageText: emptyEditorState(),
          pollingPlaceLookupTool: DEFAULT_POLLING_PLACE_LOOKUP_TOOL
        }
      ];

export default function Questions({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [questionIndex, setQuestionIndex] = useState(0);

  const [addCalleeField] = useMutation<
    campaignCreateCalleeField,
    campaignCreateCalleeFieldVariables
  >(addCalleeFieldToCampaign);

  const tenant = useTenant()!;

  const createCalleeField = async (name: string) => {
    const { data, errors } = await addCalleeField({
      variables: {
        campaignId: campaign.id,
        input: {
          name
        }
      }
    });
    if (!data) {
      throw errors;
    }
    sendEditEvent();
    const updatedFields = data.campaignCreateCalleeField.calleeFields;
    const newField = updatedFields.find(field => field.name === name);
    if (!newField) {
      throw new Error("Field wasn't created");
    }
    return newField;
  };

  const [editCampaignQuestions] = useMutation<
    campaignEditQuestions,
    campaignEditQuestionsVariables
  >(EDIT_CAMPAIGN_QUESTIONS, {
    onErrorSnackbarMessage: "Could not edit questions"
  });

  const sendEditEvent = useCampaignEditEvents();

  const handleSave = async (
    values: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const questions: CampaignQuestionInput[] = values.questions.map(
      question => {
        const vanSurveyQuestionId = cleanVanFieldValue(
          question.vanIntegrationSettings?.surveyQuestionId
        );
        return {
          key: question.key,
          name: question.name,
          script: JSON.stringify(
            convertToRaw(question.script.getCurrentContent())
          ),
          isRichText: true,
          questionType: question.questionType,
          answers: QUESTION_TYPES_WITH_ANSWERS.includes(question.questionType)
            ? question.answers.map((answer, i) => {
                const vanActivistCodeId = cleanVanFieldValue(
                  answer.vanIntegrationSettings?.activistCodeId
                );
                const vanSurveyResponseId = cleanVanFieldValue(
                  answer.vanIntegrationSettings?.surveyResponseId
                );
                const vanResultCodeId = cleanVanFieldValue(
                  answer.vanIntegrationSettings?.resultCodeId
                );

                return {
                  // re-key answers to be sequential, starting at 2
                  key: String(i + 2),
                  value: answer.value,
                  next: answer.next || null,
                  disposition: answer.disposition || null,
                  vanIntegrationSettings:
                    vanActivistCodeId || vanSurveyResponseId || vanResultCodeId
                      ? {
                          activistCodeId: vanActivistCodeId,
                          surveyResponseId: vanSurveyResponseId,
                          resultCodeId: vanResultCodeId
                        }
                      : null,
                  patchThruConfig: answer.patchThruConfig?.fixedNumber
                    ? {
                        fixedNumber: answer.patchThruConfig?.fixedNumber
                      }
                    : null
                };
              })
            : [],
          next: question.next,
          vanIntegrationSettings: vanSurveyQuestionId
            ? { surveyQuestionId: vanSurveyQuestionId }
            : null,
          mobilizeAmericaRsvpSettings:
            question.questionType === QuestionType.MOBILIZEAMERICA_RSVP
              ? getMobilizeAmericaEventParams(question.mobilizeAmericaEventUrl!)
              : null,
          sendMessageSettings:
            question.questionType === QuestionType.SEND_MESSAGE
              ? // TODO: don't encode callee fields
                {
                  text: smartEncodeReplace(
                    editorStateToPlainText(question.sendMessageText!)
                  )
                }
              : null,
          pollingPlaceLookupSettings:
            question.questionType === QuestionType.POLLING_PLACE_LOOKUP
              ? { lookupTool: question.pollingPlaceLookupTool! }
              : null
        };
      }
    );

    const { errors, data } = await editCampaignQuestions({
      variables: {
        campaignId: campaign.id,
        questions
      }
    });

    setSubmitting(false);
    if (!errors) {
      sendEditEvent();
      const { questions, calleeFields } = data!.campaignEditQuestions;
      resetForm({
        values: {
          questions: questions.map(question =>
            // setCalleeFields is asynchronous, so we need to explicitly pass the new callee
            // fields to make sure we deserialize the script with up-to-date callee fields
            rawQuestionToFormValues(question, calleeFields)
          )
        }
      });

      if (values.goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved questions", { variant: "info" });
      }
    }
  };

  return (
    <Formik
      initialValues={{
        questions: formikValues(campaign)
      }}
      onSubmit={handleSave}
      validationSchema={schemaForTenant(tenant, campaign.ivr)}
    >
      {({ values, dirty, setFieldValue, errors }: FormikProps<Values>) => {
        const currentQuestion = values.questions[questionIndex];
        function onGoToQuestion(questionKey: string) {
          setQuestionIndex(
            values.questions.findIndex(q => q.key === questionKey)
          );
        }

        return (
          <Form>
            <RouteLeavingGuard when={dirty} />
            <h2>Questions</h2>
            {campaign.structuredDispositionsCopyMigration && (
              <Alert severity="warning" className={classes.alert}>
                This campaign has been altered to use the standard first
                question format. Please add an Intro Script, adjust the response
                options if desired, and select your preferred “Action” for each
                response option below.{" "}
                <a
                  href="https://support.scaletowin.com/standardized-first-question-format"
                  target="_BLANK"
                >
                  Learn more in our guide.
                </a>
              </Alert>
            )}
            <FormikErrorBanner
              renderBanner={(errors: FormikErrors<Values>) => {
                if (!errors.questions) {
                  return "Please fix the errors indicated below";
                }

                const questionsWithErrors = values.questions
                  .map((question, questionIndex) => ({
                    question,
                    questionIndex,
                    error: errors.questions?.[questionIndex]
                  }))
                  .filter(({ error }) => !!error);

                return (
                  <div>
                    <div>Please fix the errors on the following questions:</div>
                    <ul>
                      {questionsWithErrors.map(
                        ({ question, questionIndex, error }) => (
                          <li key={questionIndex}>
                            <a
                              href="#"
                              onClick={evt => {
                                evt.preventDefault();
                                setQuestionIndex(questionIndex);
                              }}
                            >
                              Question {questionIndex + 1}{" "}
                              {question.name ? `(${question.name})` : ""}
                              {typeof error === "string" ? `: ${error}` : null}
                            </a>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                );
              }}
            />
            <FieldArray name="questions">
              {({ push, remove }: ArrayHelpers) => {
                const onAddQuestion = () => {
                  const key = uuidv4();

                  push({
                    __typename: "CampaignQuestion",
                    answers: [],
                    key,
                    name: "",
                    questionType: QuestionType.SINGLE_CHOICE,
                    script: emptyEditorState(),
                    mobilizeAmericaEventUrl: "",
                    sendMessageText: emptyEditorState(),
                    pollingPlaceLookupTool: DEFAULT_POLLING_PLACE_LOOKUP_TOOL
                  });

                  setQuestionIndex(values.questions.length);

                  return key;
                };

                const onRemoveQuestion = () => {
                  const { key } = values.questions[questionIndex];
                  remove(questionIndex);

                  values.questions.forEach((question, qi) => {
                    if (question.next === key) {
                      setFieldValue(`questions[${qi}].next`, null);
                    }
                    question.answers.forEach((answer, ai) => {
                      if (answer.next === key) {
                        setFieldValue(
                          `questions[${qi}].answers[${ai}].next`,
                          null
                        );
                      }
                    });
                  });

                  setQuestionIndex(questionIndex - 1);
                };

                const onCreateFollowupQuestion = (
                  questionKey: string,
                  answerIndex?: number
                ) => {
                  const questionIndex = values.questions.findIndex(
                    q => q.key === questionKey
                  );
                  const newQuestionKey = onAddQuestion();

                  const fieldName =
                    answerIndex == null
                      ? `questions[${questionIndex}].next`
                      : `questions[${questionIndex}].answers[${answerIndex}].next`;

                  setFieldValue(fieldName, newQuestionKey);
                };

                return (
                  <>
                    <div className={classes.questionNavigation}>
                      {values.questions.length > 0 && (
                        // TODO: use components/form/Select
                        <FormControl className={classes.questionDropdown}>
                          <MuiSelect
                            value={questionIndex}
                            onChange={evt => {
                              const { value } = evt.target;
                              if (value === "__new__") {
                                onAddQuestion();
                              } else {
                                setQuestionIndex(Number(value));
                              }
                            }}
                          >
                            {values.questions.map((question, questionIndex) => (
                              <MenuItem
                                key={question.key}
                                value={questionIndex}
                                className={
                                  errors?.questions?.[questionIndex]
                                    ? classes.errorMenuItem
                                    : ""
                                }
                              >
                                {`Question ${questionIndex + 1} ${
                                  question.name && `(${question.name})`
                                }`}
                              </MenuItem>
                            ))}
                            <MenuItem value="__new__">
                              + Add New Question
                            </MenuItem>
                          </MuiSelect>
                        </FormControl>
                      )}
                      <div>
                        {questionIndex > 0 && (
                          <Button
                            color="secondary"
                            onClick={onRemoveQuestion}
                            startIcon={<DeleteIcon />}
                          >
                            Remove Question
                          </Button>
                        )}
                      </div>
                    </div>

                    {currentQuestion && (
                      <Question
                        key={currentQuestion.key}
                        isFirstQuestion={
                          currentQuestion.key === FIRST_QUESTION_KEY
                        }
                        question={currentQuestion}
                        name={`questions[${questionIndex}]`}
                        campaign={campaign}
                        onCreateCalleeField={createCalleeField}
                        onGoToQuestion={onGoToQuestion}
                        onCreateFollowupQuestion={onCreateFollowupQuestion}
                      />
                    )}
                  </>
                );
              }}
            </FieldArray>

            <FormikSaveButtons continueWithoutSaving={goToNextSection} />
          </Form>
        );
      }}
    </Formik>
  );
}

export function validate({
  tenant,
  campaign,
  subaccountLink
}: {
  tenant: loggedInInfo_loggedInInfo_tenant;
  campaign: getCampaign_campaign;
  subaccountLink: (relativePath: string) => string;
}): JSX.Element | null {
  if (campaign.questions.length === 0) {
    return (
      <span>
        You must{" "}
        <Link to={subaccountLink(`/campaigns/${campaign.id}/questions`)}>
          add questions
        </Link>{" "}
        to your campaign.
      </span>
    );
  } else if (
    !schemaForTenant(tenant, campaign.ivr).isValidSync({
      questions: formikValues(campaign)
    })
  ) {
    return (
      <span>
        You must resolve issues with your{" "}
        <Link to={subaccountLink(`/campaigns/${campaign.id}/questions`)}>
          questions
        </Link>{" "}
        before starting the campaign.
      </span>
    );
  }

  return null;
}
